import { useContext, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Column,
  Content,
  DataTable,
  DataTableSkeleton,
  DatePicker,
  DatePickerInput,
  Dropdown,
  Grid,
  Modal,
  Row,
  Search,
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  Tile,
} from "carbon-components-react";
import {
  Edit20,
  HospitalBed20,
  Search16,
  Receipt20,
} from "@carbon/icons-react";
import dayjs from "dayjs";
import useSWR from "swr";

import { URL, Fetcher as F } from "../../api";
import { DateTimeFormat } from "../../utils/datetime";
import {
  SchemaContext,
  UserContext,
  CentersContext,
  StatusContext,
} from "../../context";
import { SchemaEditor } from "../../components/Editor";
import { useUserData, useQuery } from "../../hooks";
import * as A from "../../utils/array";
import { defaultPageSize, HousingViewer } from "../../components/Viewer";

const headers = [
  {
    key: "center",
    name: "館別",
  },
  {
    key: "room",
    name: "房名",
  },
  {
    key: "puerpera_name",
    name: "媽媽姓名",
  },
  {
    key: "checkin_date",
    name: "入住日",
  },
  {
    key: "checkout_date",
    name: "退房日",
  },
  {
    key: "fetus",
    name: "胎次",
  },
  {
    key: "book",
    name: "床段",
  },
  {
    key: "ch_history",
    name: "紀錄",
  },
];

const createQueryString = ({ center, puerperaName, roomName, queryDate }) => {
  const params = new URLSearchParams();
  params.append(
    "expand",
    "bookRoom.puerpera,bookRoom.first_brs.room.center,bookRoom.latest_brs.room.center,latest_foodvendor"
  );
  if (center !== undefined) {
    const cid = center.id;
    if (cid !== 0) {
      params.append("center", cid);
    }
  }
  if (puerperaName !== undefined) {
    params.append("puerpera_name", puerperaName);
  }
  if (roomName !== undefined) {
    params.append("room_name", roomName);
  }
  if (queryDate !== undefined) {
    const date = dayjs(queryDate).format(DateTimeFormat.date);
    if (date) {
      params.append("date", date);
    }
  }
  params.append("offset", 0);
  params.append("limit", defaultPageSize);
  // fields=id,name
  return params.toString();
};

export const BatchChangeFoodVendor = () => {
  const { list: centerList } = useContext(CentersContext);

  const {
    token: { access },
  } = useContext(UserContext);

  const q = useQuery();
  let date = q.get("_date");
  if (date === "" || date === null) {
    date = dayjs().format(DateTimeFormat.date);
  }

  const [center, setCenter] = useState();
  const [puerperaName, setPuerperName] = useState();
  const [queryDate, setQueryDate] = useState(date);

  const queryString = useMemo(
    () => createQueryString({ center, queryDate }),
    [center, queryDate]
  );

  const [realyQuery, setRealyQuery] = useState(queryString);

  const housingURL = `${URL.housing}?${realyQuery}`;
  const { data: housingListResponse } = useSWR(
    access && [housingURL],
    F.withToken
  );

  const housingListData = housingListResponse?.results ?? [];
  const isLoading = !housingListResponse;

  return (
    <Content>
      <Breadcrumb>
        <BreadcrumbItem href="/housing/">住房管理</BreadcrumbItem>
        <BreadcrumbItem href="" isCurrentPage>
          膳食廠商批次更換
        </BreadcrumbItem>
      </Breadcrumb>

      <Tile>
        膳食廠商批次更換
        <Grid>
          <Row>
            <Column md={1}>
              <Dropdown
                id="center-selector"
                titleText="館別"
                label="全部"
                items={A.insert(centerList, { id: 0, name: "全部" }, 0)}
                itemToString={(item) => item.name}
                selectedItem={center}
                onChange={(v) => {
                  setCenter(v.selectedItem);
                }}
              />
            </Column>
            <Column>
              <DatePicker
                dateFormat="Y/m/d"
                datePickerType="single"
                value={date}
                onChange={([date]) => {
                  setQueryDate(date);
                }}
              >
                <DatePickerInput
                  id="checkin_date"
                  placeholder="yyyy/mm/dd"
                  labelText="日期"
                />
              </DatePicker>
            </Column>
            <Column>
              <Search
                size="lg"
                labelText="搜尋產婦姓名"
                placeholder="產婦姓名"
                onChange={(e) => {
                  const v = e.target.value;
                  setPuerperName(v);
                }}
              />
            </Column>
            <Column sm={1}>
              <Button
                renderIcon={Search16}
                iconDescription="搜尋"
                hasIconOnly
                onClick={() => {
                  const queryString = createQueryString({
                    center,
                    puerperaName,
                    queryDate,
                  });
                  setRealyQuery(queryString);
                }}
              ></Button>
            </Column>
          </Row>
        </Grid>
      </Tile>

      {isLoading ? (
        <DataTableSkeleton showHeader={false} />
      ) : (
        <BatchChangeFoodVendorTable data={housingListData} />
      )}
    </Content>
  );
};

function BatchChangeFoodVendorTable({ data }) {
  const { changeFoodVendorHistory: schema } = useContext(SchemaContext) || {};
  const { foodMeals, foodVendor: foodVendorList } = useContext(StatusContext);
  const {
    token: { access },
  } = useContext(UserContext);

  const user = useUserData();

  const cleanData = data.map(({ bookRoom, latest_foodvendor, ...other }) => {
    let room = bookRoom?.today_brs?.room;
    if (bookRoom?.latest_brs?.room) {
      room = bookRoom?.latest_brs?.room;
    } else if (bookRoom?.first_brs?.room) {
      room = bookRoom?.first_brs?.room;
    }

    return {
      ...other,
      puerpera_name: bookRoom?.puerpera?.name ?? "",
      checkin_date: bookRoom?.first_brs?.checkin_date,
      checkout_date: bookRoom?.latest_brs?.checkout_date,
      room: room?.name ?? "",
      center: room?.center?.name ?? "",
      foodvendor: latest_foodvendor ?? {},
    };
  });

  const [changeFoodVendor, setChangeFoodVendor] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [editModalStatus, setEditModalStatus] = useState("inactive");

  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openInfoStatus, setOpenInfoStatus] = useState("inactive");
  const [selectedHousingId, setSelectedHousingId] = useState(null);

  const housingDetailExpand =
    "events,foodvendor,bookRoom.puerpera,puerperaNote," +
    "bookRoom.contract,bookRoom.first_brs,bookRoom.latest_brs.room.center";
  const housingDetailURL = selectedHousingId
    ? `${URL.housing}${selectedHousingId}/?expand=${housingDetailExpand}`
    : null;
  const { data: oneHousing } = useSWR(
    openInfoModal && access && housingDetailURL ? [housingDetailURL] : null,
    F.withToken
  );
  const isLoadingForOneHousing = openInfoModal && !oneHousing;

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [confirmModalStatus, setConfirmModalStatus] = useState("inactive");
  const [preivewRows, setPreivewRows] = useState([]);

  const previewHeaders = [
    {
      key: "room",
      name: "房名",
    },
    {
      key: "puerpera_name",
      name: "媽媽名",
    },
    {
      key: "origin_foodvendor",
      name: "原廠商",
    },
    {
      key: "new_foodvendor",
      name: "新廠商",
    },
    {
      key: "food_meal",
      name: "那一餐",
    },
  ];

  const handlePreview = async () => {
    setEditModalStatus("finished");
    setOpenConfirmModal(true);
    const dataMap = new Map(cleanData.map((row) => [row.id, row]));
    const newVendor = (foodVendorList ?? []).find(
      (x) => x.id === changeFoodVendor.foodvendor
    );
    const newMeal = (foodMeals ?? []).find(
      (x) => x.id === changeFoodVendor.start_meal
    );
    const previewRows = selectedItems.map((housingId) => {
      const row = dataMap.get(housingId) ?? {};
      return {
        id: housingId,
        room: row.room ?? "",
        puerpera_name: row.puerpera_name ?? "",
        origin_foodvendor: row.foodvendor?.name ?? "",
        new_foodvendor: newVendor?.name ?? "",
        food_meal: newMeal?.name ?? "",
      };
    });
    setPreivewRows(previewRows);
  };

  const handleBatchCreate = async () => {
    // console.debug(`cc`, changeFoodVendor, selectedItems);
    setConfirmModalStatus("active");
    const payload = {
      ...changeFoodVendor,
      housings: selectedItems,
    };
    const result = await F.post(URL.batch.changeFoodVendorHistory, payload);
    console.debug(`result`, result);
    setConfirmModalStatus("finished");
    setOpenConfirmModal(false);
    setOpenEditModal(false);
  };

  return (
    <>
      <Modal
        passiveModal
        open={openInfoModal}
        modalHeading="床段資訊"
        loadingStatus={openInfoStatus}
        onRequestClose={() => setOpenInfoModal(false)}
        onSecondarySubmit={() => setOpenInfoModal(false)}
        onRequestSubmit={() => {
          setOpenInfoModal(false);
          setOpenInfoStatus("finished");
        }}
      >
        <HousingViewer loading={isLoadingForOneHousing} data={oneHousing} />
      </Modal>
      <Modal
        open={openEditModal}
        modalHeading="變更膳食廠商相關資訊"
        primaryButtonText="預覽"
        secondaryButtonText="取消"
        loadingStatus={editModalStatus}
        onRequestClose={() => setOpenEditModal(false)}
        onSecondarySubmit={() => setOpenEditModal(false)}
        onRequestSubmit={handlePreview}
      >
        <SchemaEditor
          fields={schema.fields}
          excludes={[
            "id",
            "created",
            "modified",
            "employee_id",
            "housing",
            "activate_date",
            "deactivate_date",
            "status",
            "bookroomschedule",
            "comment",
          ]}
          requiredFields={["start_date", "end_date"]}
          data={changeFoodVendor}
          onChange={(x) => {
            if (!user) return;
            setChangeFoodVendor({
              ...x,
              employee_id: user.employee_id,
            });
          }}
        />
      </Modal>
      <Modal
        open={openConfirmModal}
        modalHeading="確認變更結果"
        primaryButtonText="確認"
        secondaryButtonText="取消"
        loadingStatus={confirmModalStatus}
        onRequestClose={() => setOpenConfirmModal(false)}
        onSecondarySubmit={() => setOpenConfirmModal(false)}
        onRequestSubmit={handleBatchCreate}
      >
        <DataTable rows={preivewRows} headers={previewHeaders}>
          {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({ header })}
                    >
                      {header.name}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    {row.cells.map((cell) => {
                      const { header = "" } = cell && cell.info;
                      let content;
                      switch (header) {
                        case "room":
                        case "puerpera_name":
                        case "origin_foodvendor":
                        case "new_foodvendor":
                        case "food_meal":
                          content = cell.value;
                          break;
                        default:
                          content = <span>?</span>;
                      }
                      return <TableCell key={cell.id}>{content}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DataTable>
      </Modal>
      <DataTable rows={cleanData} headers={headers}>
        {({
          rows,
          headers,
          selectedRows,
          getHeaderProps,
          getRowProps,
          getSelectionProps,
          getBatchActionProps,
          getTableProps,
          getToolbarProps,
          getTableContainerProps,
        }) => (
          <TableContainer {...getTableContainerProps()}>
            <TableToolbar {...getToolbarProps()} aria-label="住房產婦">
              <TableBatchActions {...getBatchActionProps()}>
                <TableBatchAction
                  tabIndex={
                    getBatchActionProps().shouldShowBatchActions ? 0 : -1
                  }
                  renderIcon={Edit20}
                  onClick={() => {
                    setOpenEditModal(true);
                    setEditModalStatus("active");
                    setOpenInfoModal(false);
                    setOpenConfirmModal(false);
                    const ids = selectedRows.map(({ id, ...other }) => {
                      return id;
                    });
                    setSelectedItems(ids);
                  }}
                >
                  批次更換廠商
                </TableBatchAction>
              </TableBatchActions>
            </TableToolbar>

            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  <TableSelectAll {...getSelectionProps()} />
                  {headers.map((header) => (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({ header })}
                    >
                      {header.name}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    <TableSelectRow {...getSelectionProps({ row })} />
                    {row.cells.map((cell) => {
                      const { header = "" } = cell && cell.info;
                      const rid = cell.id.split(":")[0];
                      let content;
                      switch (header) {
                        case "checkin_date":
                        case "checkout_date":
                          if (cell.value !== null) {
                            content = dayjs(cell.value).format(
                              DateTimeFormat.date
                            );
                          } else {
                            content = cell.value;
                          }
                          break;
                        case "center":
                        case "room":
                        case "puerpera_name":
                        case "fetus":
                          content = cell.value;
                          break;
                        case "book":
                          content = (
                            <Button
                              kind="ghost"
                              renderIcon={HospitalBed20}
                              iconDescription="床段"
                              onClick={() => {
                                setSelectedHousingId(rid);
                                setOpenInfoModal(true);
                              }}
                            />
                          );
                          break;

                        case "ch_history":
                          content = (
                            <Button
                              kind="ghost"
                              renderIcon={Receipt20}
                              iconDescription="記錄"
                              as={Link}
                              to={`/housing/foodVendor/${rid}`}
                              target="_blank"
                              rel="noreferrer"
                            />
                          );
                          break;
                        default:
                          content = <span>?</span>;
                      }
                      return <TableCell key={cell.id}>{content}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    </>
  );
}
